import React, { useState } from "react";
import { motion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { Share2, Copy, Check, MessageCircle } from "lucide-react";
import { useApp } from "../AppContext";
import { toast } from "sonner";
import { FlagStripe } from "./FlagAccents";

const ShareSection = () => {
    const { t, branding } = useApp();
    const [copied, setCopied] = useState(false);

    // The canonical public storefront URL — used for QR codes, WhatsApp share
    // and the copy-link button. Hard-coded to the production multi-tenant
    // route so a physical QR (or a friend's pasted link) always lands on
    // /r/delicias regardless of where this build is rendering.
    const siteUrl = "https://deliciasbistro.com/r/delicias";
    const qrUrl = siteUrl;

    const waMessage = `${t.share.whatsappMessage} ${siteUrl}`;
    const waHref = `https://wa.me/?text=${encodeURIComponent(waMessage)}`;

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(siteUrl);
            setCopied(true);
            toast.success(t.share.copied);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error(t.common.error);
        }
    };

    return (
        <section
            id="share"
            data-testid="share-section"
            className="relative py-16 sm:py-20 bg-gradient-to-b from-earth-50 to-earth-100"
        >
            {branding.decorations_enabled && (
                <FlagStripe thickness="h-1.5" className="absolute top-0 left-0 right-0 opacity-70" />
            )}
            <div className="max-w-6xl mx-auto px-6 sm:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="grid md:grid-cols-2 gap-10 items-center bg-white rounded-3xl shadow-xl border border-earth-200 p-8 sm:p-12 overflow-hidden relative"
                >
                    {branding.decorations_enabled && (
                        <div
                            className="absolute -top-12 -right-12 w-48 h-48 rounded-full opacity-10"
                            style={{ background: branding.brand_color_primary }}
                        />
                    )}

                    <div className="relative">
                        <span
                            className="inline-block text-[11px] uppercase tracking-[0.18em] font-bold px-3 py-1 rounded-full"
                            style={{
                                color: branding.brand_color_primary,
                                background: `${branding.brand_color_primary}15`,
                            }}
                            data-testid="share-tag"
                        >
                            {t.share.tag}
                        </span>
                        <h2
                            className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-earth-900 mt-4 leading-tight"
                            data-testid="share-title"
                        >
                            {t.share.title}
                        </h2>
                        <p className="text-earth-600 mt-4 text-base leading-relaxed">
                            {t.share.subtitle}
                        </p>

                        <div className="flex flex-col sm:flex-row gap-3 mt-7">
                            <a
                                href={waHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="share-whatsapp-btn"
                                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#25D366] hover:bg-[#1ebe5a] text-white font-semibold shadow-lg shadow-green-500/20 transition-all hover:scale-[1.02] active:scale-100"
                            >
                                <MessageCircle className="w-5 h-5" />
                                {t.share.whatsappBtn}
                            </a>
                            <button
                                type="button"
                                onClick={handleCopy}
                                data-testid="share-copy-btn"
                                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-earth-900 hover:bg-earth-800 text-white font-semibold transition-all hover:scale-[1.02] active:scale-100"
                            >
                                {copied ? (
                                    <>
                                        <Check className="w-5 h-5" /> {t.share.copied}
                                    </>
                                ) : (
                                    <>
                                        <Copy className="w-5 h-5" /> {t.share.copyBtn}
                                    </>
                                )}
                            </button>
                        </div>

                        <div className="mt-5 text-xs text-earth-500 flex items-center gap-2">
                            <Share2 className="w-3.5 h-3.5" />
                            <span className="truncate" data-testid="share-url-display">
                                {siteUrl}
                            </span>
                        </div>
                    </div>

                    <div className="flex flex-col items-center">
                        <div
                            className="relative rounded-3xl p-5 sm:p-6 shadow-lg border border-earth-200 bg-white"
                            data-testid="share-qr-card"
                        >
                            <div
                                className="absolute inset-0 rounded-3xl opacity-90 -z-0"
                                style={{
                                    background: `linear-gradient(135deg, ${branding.brand_color_primary}22, ${branding.brand_color_accent}22)`,
                                }}
                            />
                            <div className="relative">
                                <QRCodeSVG
                                    value={qrUrl}
                                    size={208}
                                    bgColor="#ffffff"
                                    fgColor={branding.brand_color_primary || "#0f172a"}
                                    level="M"
                                    includeMargin={false}
                                    data-testid="share-qr-svg"
                                />
                                <div
                                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center text-white font-serif font-bold text-sm shadow-md ring-4 ring-white"
                                    style={{ background: branding.brand_color_primary }}
                                    aria-hidden="true"
                                >
                                    {branding.brand_initials || "DD"}
                                </div>
                            </div>
                        </div>
                        <p className="text-xs text-earth-500 mt-4 font-medium">
                            {t.share.scanHint}
                        </p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default ShareSection;
